import { Tabs } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Dimensions, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { HapticTab } from '@/components/haptic-tab';
import { Colors } from '@/constants/theme';
import { SIDEBAR_BREAKPOINT, SIDEBAR_WIDTH } from '@/constants/layout';
import { useColorScheme } from '@/hooks/use-color-scheme';

const TAB_BAR_MARGIN_H = 20;
const TAB_BAR_BOTTOM_MARGIN = 12;
const TAB_BAR_BORDER_RADIUS = 28;
// Android needs extra height so tab labels aren't clipped (different default padding/line height)
const TAB_BAR_HEIGHT = Platform.OS === 'android' ? 72 : 64;

export default function TabLayout() {
  const insets = useSafeAreaInsets();
  const [windowWidth, setWindowWidth] = useState(Dimensions.get('window').width);
  const colorScheme = useColorScheme() ?? 'light';
  const themeColors = Colors[colorScheme];

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setWindowWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  const isDesktopWeb = Platform.OS === 'web' && windowWidth >= SIDEBAR_BREAKPOINT;
  // On iOS use a smaller margin to avoid unusual gap below the tab bar; keep existing behavior elsewhere
  const bottomMargin = Platform.OS === 'ios' ? 8 : TAB_BAR_BOTTOM_MARGIN;
  const tabBarBottom = insets.bottom > 0 ? insets.bottom + bottomMargin : TAB_BAR_BOTTOM_MARGIN + 16;

  const tabBarStyle = isDesktopWeb
    ? {
        backgroundColor: themeColors.tabBarBackground,
        borderRightWidth: 1,
        borderRightColor: 'rgba(0, 0, 0, 0.08)',
        width: SIDEBAR_WIDTH,
      }
    : {
        position: 'absolute' as const,
        left: TAB_BAR_MARGIN_H,
        right: TAB_BAR_MARGIN_H,
        bottom: tabBarBottom,
        height: TAB_BAR_HEIGHT,
        borderRadius: TAB_BAR_BORDER_RADIUS,
        overflow: 'hidden' as const,
        backgroundColor: themeColors.tabBarBackground,
        borderWidth: 1,
        borderColor: 'rgba(0, 0, 0, 0.08)',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
        elevation: Platform.OS === 'android' ? 0 : 12,
        // Prevent tab labels from being clipped on Android
        ...(Platform.OS === 'android' && { paddingBottom: 8 }),
      };

  return (
    <Tabs
      initialRouteName="recipes"
      screenOptions={{
        tabBarActiveTintColor: themeColors.tabIconSelected,
        tabBarInactiveTintColor: themeColors.tabIconDefault,
        tabBarActiveBackgroundColor: isDesktopWeb ? themeColors.tabBarActiveBackground : 'transparent',
        tabBarLabelStyle: { fontSize: 12, fontWeight: '600' },
        tabBarItemStyle: isDesktopWeb ? { borderRadius: 12, marginHorizontal: 8, marginVertical: 4 } : undefined,
        tabBarPosition: isDesktopWeb ? ('left' as const) : ('bottom' as const),
        headerShown: false,
        tabBarButton: HapticTab,
        tabBarStyle,
      }}>
      <Tabs.Screen
        name="index"
        options={{ href: null }}
      />
      <Tabs.Screen
        name="recipes"
        options={{
          title: 'Recipes',
          tabBarIcon: ({ color, size }) => <Ionicons name="home" size={size} color={color} />,
          tabBarButton: (props) => <HapticTab {...props} testID="tab-recipes" />,
        }}
      />
      <Tabs.Screen
        name="grocery-lists"
        options={{
          title: 'Lists',
          tabBarIcon: ({ color, size }) => <Ionicons name="cart" size={size} color={color} />,
          tabBarButton: (props) => <HapticTab {...props} testID="tab-lists" />,
        }}
      />
      <Tabs.Screen
        name="settings"
        options={{
          title: 'Settings',
          tabBarIcon: ({ color, size }) => <Ionicons name="settings" size={size} color={color} />,
          tabBarButton: (props) => <HapticTab {...props} testID="tab-settings" />,
        }}
      />
      <Tabs.Screen
        name="explore"
        options={{
          href: null, // Hide from tabs
        }}
      />
    </Tabs>
  );
}
